import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import cond from 'lodash/cond';
import constant from 'lodash/constant';
import get from 'lodash/get';
import isInteger from 'lodash/isInteger';
import isNumber from 'lodash/isNumber';
import stubTrue from 'lodash/stubTrue';
import toNumber from 'lodash/toNumber';

import HiddenInput from 'common/components/HiddenInput';

import {
    SuiCheckbox,
    SuiCheckboxGroup,
    SuiDatePickerNew,
    SuiDropdown,
    SuiDropdownOption,
    SuiRadio,
    SuiRadioGroup,
    SuiTextInput,
    SuiTimePicker,
} from '@splashthat/sui';

import { basePlaceholderOpacity, baseSpacer } from 'common/styles/variables';

import {
    INPUT_TYPE_ADDRESS,
    INPUT_TYPE_CAPTCHA,
    INPUT_TYPE_CHECKBOX,
    INPUT_TYPE_DATE,
    INPUT_TYPE_EMAIL,
    INPUT_TYPE_EVENT_OPS_2,
    INPUT_TYPE_EVENT_OPS_RESOURCE,
    INPUT_TYPE_FILE,
    INPUT_TYPE_HIDDEN,
    INPUT_TYPE_MULTI_SELECT,
    INPUT_TYPE_NUMBER,
    INPUT_TYPE_PARAGRAPH,
    INPUT_TYPE_PHONE_NUMBER,
    INPUT_TYPE_RADIO,
    INPUT_TYPE_SELECT,
    INPUT_TYPE_SOCIAL_RSVP,
    INPUT_TYPE_TEXT,
    INPUT_TYPE_TIME,
    INPUT_TYPE_URL,
} from 'common/entities/formNode/inputTypes';

import {
    NODE_NAME_ADDITIONAL_GUEST_COUNT,
    NODE_NAME_ADDITIONAL_GUEST_EMAIL_REGEXP,
} from 'common/entities/formNode/nodeNames';

import { NUMBER_MAX, NUMBER_MIN, TIME_MAX, TIME_MIN } from 'common/entities/formNode/nodeConfig';

import LocationSearchInput from 'common/components/LocationSearchInput';
import Recaptcha from 'common/components/Recaptcha';
import { DEFAULT_ERRORS_COLOR } from 'common/entities/form/styleConfiguration/defaultValues';
import QuestionView from './QuestionView';
import SuiFileWrapper from './SuiFileWrapper';
import CheckboxIncludeOther from './CheckboxIncludeOther';
import DropdownIncludeOther from './DropdownIncludeOther';
import RadioIncludeOther from './RadioIncludeOther';
import SanitizedHTML from '../../SanitizedHTML';
import SocialRsvp from './SocialRsvp';
import EventOpsResources from './EventOpsResources';
import EventOps2 from './EventOps2';
import { getInnerHtmlAndInsertAsteriskIfRequired } from '../../../utils/getInnerHtmlAndInsertAsteriskIfRequired';
import { getDateRangeEffectiveLimits } from '../../../utils/dateRanges';

import './QuestionNode.styl';

const AsyncFileUpload = SuiFileWrapper;

const NOT_AVAILABLE = 'not available';
const BASE_CLASS_NAME = 'QuestionNode';
const SOME_REMAINING = (quantity) => `${quantity} remaining`;

// This is the separator that Splash's back-end uses to separate multi select values.
const MULTIPLE_SELECTED_VALUE_SEPARATOR = '||';

const LinkURL = styled.a`
    margin-left: 5px;
`;

const Message = styled.div`
    opacity: ${basePlaceholderOpacity};
    margin-top: ${baseSpacer};
`;

export const UPDATE_ADDITIONAL_GUEST_COUNT_MESSAGE = `Guests will be removed from the guest list if you reduce
your guest count. Available guests count is determined by
event settings and capacity.`;

export const UPDATE_ADDITIONAL_GUEST_EMAIL_MESSAGE = 'If their email is changed, your guest will be replaced.';
/**
 * Core implemnetation of question.
 * This component should only have all behavior implementation and no UI or layout.
 */
export default class QuestionNode extends PureComponent {
    static propTypes = {
        nodeId: PropTypes.string.isRequired,
        inputType: PropTypes.string.isRequired,
        value: PropTypes.any,
        initialValue: PropTypes.any,
        label: PropTypes.node.isRequired,
        labelMarkup: PropTypes.string,
        helperText: PropTypes.string.isRequired,
        placeholder: PropTypes.string,
        visible: PropTypes.bool.isRequired,
        required: PropTypes.bool.isRequired,
        error: PropTypes.bool,
        touched: PropTypes.bool.isRequired,
        disabled: PropTypes.bool.isRequired,
        includeOther: PropTypes.bool,
        config: PropTypes.object,
        setFieldValue: PropTypes.func.isRequired,
        onBlur: PropTypes.func.isRequired,
        errorColor: PropTypes.string,
        code: PropTypes.string,
        name: PropTypes.string,
        isRegistrationOpen: PropTypes.bool.isRequired,
        isUpdating: PropTypes.bool.isRequired,
        isProtected: PropTypes.bool.isRequired,
    };

    static defaultProps = {
        value: null,
        initialValue: null,
        error: null,
        placeholder: null,
        config: null,
        includeOther: undefined,
        labelMarkup: '',
        errorColor: DEFAULT_ERRORS_COLOR,
        code: '',
        name: '',
    };

    handleChange = (event, value = event.target.value) => {
        this.props.setFieldValue(this.props.nodeId, value);
    };

    handleInputBlur = (event, value = event.target.value) => {
        this.props.onBlur(event, value);
        if (this.props.inputType === 'number') {
            const valueAsNum = toNumber(value);
            this.props.setFieldValue(
                this.props.nodeId,
                isNumber(valueAsNum) && !isInteger(valueAsNum) ? valueAsNum.toFixed(2) : value,
            );
        }
    };

    /**
     * Updates the current answer value of the address input.
     * @param {string} value
     */
    handleAddressInputChange = (value) => {
        const valueToSend = value != null && value !== '' ? { label: value } : '';
        this.props.setFieldValue(this.props.nodeId, valueToSend);
    };

    /**
     * Updates the stored answer with the venue object details, which contains address, city,
     * country, lat, lng, name, state and zipCode.
     * @param {Object} venueObject
     */
    handleAddressVenueObjectChange = (venueObject) => {
        this.props.setFieldValue(this.props.nodeId, {
            ...this.props.value,
            ...venueObject,
        });
    };

    /**
     * @param {string} available
     * @return {boolean}
     */
    isOptionHidden = (available) => {
        const hideNotAvailableResponses = get(this.props.config, 'hideNotAvailableResponses', false);
        return hideNotAvailableResponses && available <= 0;
    };

    /**
     * Returns the label including the required flag if necessary.
     */
    renderLabel = () => {
        const { label, labelMarkup, required, errorColor } = this.props;
        const innerHtml = getInnerHtmlAndInsertAsteriskIfRequired(label, labelMarkup, required, errorColor);
        return <SanitizedHTML innerHtml={innerHtml} />;
    };

    renderHelperText = () => <SanitizedHTML innerHtml={this.props.helperText} />;

    renderComponentByType = {
        [INPUT_TYPE_PARAGRAPH]: this.renderParagraph,
        [INPUT_TYPE_CHECKBOX]: this.renderCheckbox,
        [INPUT_TYPE_DATE]: this.renderDate,
        [INPUT_TYPE_TIME]: this.renderTime,
        [INPUT_TYPE_FILE]: this.renderFile,
        [INPUT_TYPE_ADDRESS]: this.renderAddress,
        [INPUT_TYPE_HIDDEN]: this.renderHidden,
        [INPUT_TYPE_RADIO]: this.renderRadio,
        [INPUT_TYPE_SELECT]: this.renderDropdown,
        [INPUT_TYPE_MULTI_SELECT]: this.renderMultiSelect,
        [INPUT_TYPE_TEXT]: this.renderText,
        [INPUT_TYPE_EMAIL]: this.renderEmail,
        [INPUT_TYPE_PHONE_NUMBER]: this.renderPhoneNumber,
        [INPUT_TYPE_URL]: this.renderUrl,
        [INPUT_TYPE_NUMBER]: this.renderNumber,
        [INPUT_TYPE_CAPTCHA]: this.renderCaptcha,
        [INPUT_TYPE_EVENT_OPS_RESOURCE]: this.renderEventOpsResources,
        [INPUT_TYPE_EVENT_OPS_2]: this.renderEventOps2,
        [INPUT_TYPE_SOCIAL_RSVP]: this.renderSocialRsvp,
    };

    renderParagraph() {
        return (
            <SuiTextInput
                name={this.props.nodeId}
                label={this.renderLabel()}
                helperText={this.renderHelperText()}
                onChange={this.handleChange}
                placeholder={this.props.placeholder}
                onBlur={this.props.onBlur}
                value={this.props.value}
                disabled={this.props.disabled}
                variation={!this.props.touched && this.props.error ? 'error' : ''}
                fullWidth
                multiLine
                rows={2}
                aria-required={this.props.required || undefined}
                data-qa={`${BASE_CLASS_NAME}__${this.props.code}`}
                data-field-name={this.props.code}
            />
        );
    }

    renderCheckboxLabel() {
        const {
            label,
            labelMarkup,
            required,
            errorColor,
            config: { linkUrl, linkLabel },
        } = this.props;

        const hasAttachmentLink = linkUrl || linkLabel;
        const innerHtml = getInnerHtmlAndInsertAsteriskIfRequired(label, labelMarkup, required, errorColor);

        return (
            <span className={`${BASE_CLASS_NAME}__checkbox-label`}>
                <SanitizedHTML innerHtml={innerHtml} />
                {hasAttachmentLink && (
                    <LinkURL href={linkUrl || '#'} target={linkUrl ? '_blank' : '_self'}>
                        {linkLabel}
                    </LinkURL>
                )}
            </span>
        );
    }

    renderCheckbox() {
        return (
            <SuiCheckbox
                name={this.props.nodeId}
                label={this.renderCheckboxLabel()}
                helperText={this.renderHelperText()}
                onChange={this.handleChange}
                onBlur={this.props.onBlur}
                value={this.props.value || undefined}
                disabled={this.props.disabled}
                variation={!this.props.touched && this.props.error ? 'error' : ''}
                fullWidth
                baselineAlignment
                checked={!!this.props.value && this.props.value !== '0'}
                className={`${BASE_CLASS_NAME}__checkbox`}
                aria-required={this.props.required || undefined}
                data-qa={`${BASE_CLASS_NAME}__${this.props.code}`}
                data-field-name={this.props.code}
            />
        );
    }

    renderDate() {
        const dateRange = getDateRangeEffectiveLimits(this.props.config);

        return (
            <SuiDatePickerNew
                name={this.props.nodeId}
                label={this.renderLabel()}
                helperText={this.renderHelperText()}
                onChange={this.handleChange}
                onBlur={this.props.onBlur}
                value={this.props.value}
                disabled={this.props.disabled}
                variation={!this.props.touched && this.props.error ? 'error' : ''}
                fullWidth
                disabledDays={[dateRange]}
                disableInternalErrors
                data-qa={`${BASE_CLASS_NAME}__${this.props.code}`}
                data-field-name={this.props.code}
                aria-required={this.props.required || undefined}
            />
        );
    }

    renderTime() {
        const timeMax = get(this.props.config, TIME_MAX, null);
        const timeMin = get(this.props.config, TIME_MIN, null);

        return (
            <SuiTimePicker
                name={this.props.nodeId}
                label={this.renderLabel()}
                helperText={this.renderHelperText()}
                onChange={this.handleChange}
                onBlur={this.props.onBlur}
                value={this.props.value}
                disabled={this.props.disabled}
                variation={!this.props.touched && this.props.error ? 'error' : ''}
                fullWidth
                disabledTimes={{
                    before: timeMin,
                    after: timeMax,
                }}
                aria-required={this.props.required || undefined}
                data-qa={`${BASE_CLASS_NAME}__${this.props.code}`}
                data-field-name={this.props.code}
            />
        );
    }

    renderFile() {
        const { label, labelMarkup } = this.props;
        const getLabelText = cond([
            [() => typeof label === 'string', () => label.trim() || null],
            [
                () => Boolean(labelMarkup),
                () =>
                    labelMarkup
                        .replace(/<[^>]+>/g, ' ')
                        .replace(/\s+/g, ' ')
                        .trim() || null,
            ],
            [stubTrue, constant(null)],
        ]);
        const labelText = getLabelText();
        const ariaLabel = labelText ? `${labelText}. Drag and drop your file or Browse` : undefined;

        return (
            <AsyncFileUpload
                name={this.props.nodeId}
                label={this.renderLabel()}
                text={this.props.helperText}
                onChange={this.handleChange}
                setFieldValue={this.props.setFieldValue}
                onBlur={this.props.onBlur}
                value={this.props.value}
                disabled={this.props.disabled}
                aria-required={this.props.required || undefined}
                aria-label={ariaLabel}
                fullWidth
            />
        );
    }

    renderAddress() {
        // Value should come as an object. However, it form was pre-populated from a Cake submission
        // it will be loaded initially as a string.
        const valueLabel = typeof this.props.value === 'string' ? this.props.value : get(this.props.value, 'label');

        return (
            <LocationSearchInput
                name={this.props.nodeId}
                label={this.renderLabel()}
                onVenueObjectChange={this.handleAddressVenueObjectChange}
                onInputChange={this.handleAddressInputChange}
                onBlur={this.props.onBlur}
                value={valueLabel}
                disabled={this.props.disabled}
                variation={!this.props.touched && this.props.error ? 'error' : ''}
                fullWidth
                placeholder={this.props.placeholder}
                data-qa={`${BASE_CLASS_NAME}__${this.props.code}`}
                data-field-name={this.props.code}
                helperText={this.renderHelperText()}
                menuFooter={
                    <footer style={{ textAlign: 'right', padding: '4px' }}>
                        <img
                            src="//d24wuq6o951i2g.cloudfront.net/img/site-assets/powered_by_google_on_white.png"
                            alt="Powered by Google"
                        />
                    </footer>
                }
                aria-required={this.props.required || undefined}
            />
        );
    }

    renderHidden() {
        return (
            <HiddenInput
                name={this.props.nodeId}
                label={this.renderLabel()}
                onChange={this.handleChange}
                onBlur={this.props.onBlur}
                value={this.props.value}
                disabled={this.props.disabled}
                data-qa={`${BASE_CLASS_NAME}__${this.props.code}`}
                data-field-name={this.props.code}
                aria-required={this.props.required || undefined}
            />
        );
    }

    getOptions() {
        const result = get(this.props.config, 'options.result', []);
        const entities = get(this.props.config, 'options.entities', {});

        return result.map((optionId) => {
            const entity = entities[optionId];
            return {
                ...entity,
                // @TODO We are doing this because we have values that are not trimmed for now.
                // Remove the trimming once the fix has been live for a few months, around March 2019.
                // Jira ticket: FOR-3311.
                value: typeof entity.value === 'string' ? entity.value.trim() : entity.value,
            };
        });
    }

    getBringingAGuestOptions() {
        const result = get(this.props.config, 'options.result', []);
        const entities = get(this.props.config, 'options.entities', {});

        const { isRegistrationOpen, isUpdating } = this.props;

        const additionalGuestCount =
            typeof this.props.initialValue === 'string' ? this.props.initialValue.trim() : this.props.initialValue;

        return result
            .filter((optionId) => {
                const entity = entities[optionId];
                return !isRegistrationOpen && isUpdating
                    ? parseInt(entity.value, 10) <= parseInt(additionalGuestCount, 10)
                    : true;
            })
            .map((optionId) => {
                const entity = entities[optionId];
                return {
                    ...entity,
                    // @TODO We are doing this because we have values that are not trimmed for now.
                    // Remove the trimming once the fix has been live for a few months, around March 2019.
                    // Jira ticket: FOR-3311.
                    value: typeof entity.value === 'string' ? entity.value.trim() : entity.value,
                };
            });
    }

    renderRadio() {
        const RadioGroupComponent = this.props.includeOther ? RadioIncludeOther : SuiRadioGroup;
        const showRemainingResponses = !!get(this.props.config, 'showRemainingResponses');

        // @TODO We are doing this because we have values that are not trimmed for now.
        // Remove the trimming once the fix has been live for a few months, around March 2019.
        // Jira ticket: FOR-3311.
        const trimmedValue = typeof this.props.value === 'string' ? this.props.value.trim() : this.props.value;

        return (
            <RadioGroupComponent
                name={this.props.nodeId}
                label={this.renderLabel()}
                questionLabel={this.props.includeOther ? this.props.label : undefined}
                helperText={this.renderHelperText()}
                onChange={this.handleChange}
                onBlur={this.props.onBlur}
                value={trimmedValue}
                disabled={this.props.disabled}
                variation={!this.props.touched && this.props.error ? 'error' : ''}
                fullWidth
                aria-required={this.props.required || undefined}
                data-qa={`${BASE_CLASS_NAME}__${this.props.code}`}
                data-field-name={this.props.code}
            >
                {this.getOptions().map((option) => {
                    const { id, label, value, available, responseLimit } = option;

                    const optionLabel = this.getOptionLabel(label, showRemainingResponses ? available : null);

                    return (
                        !this.isOptionHidden(available) && (
                            <SuiRadio
                                key={id}
                                value={value}
                                checked={option.checked}
                                label={optionLabel}
                                disabled={
                                    responseLimit !== undefined && available <= 0 && this.props.initialValue !== value
                                }
                                data-qa={`${BASE_CLASS_NAME}__option-${value}`}
                            />
                        )
                    );
                })}
            </RadioGroupComponent>
        );
    }

    renderDropdown() {
        const DropdownComponent = this.props.includeOther ? DropdownIncludeOther : SuiDropdown;
        const showRemainingResponses = get(this.props.config, 'showRemainingResponses', true);

        // @TODO We are doing this because we have values that are not trimmed for now.
        // Remove the trimming once the fix has been live for a few months, around March 2019.
        // Jira ticket: FOR-3311.
        const trimmedValue = typeof this.props.value === 'string' ? this.props.value.trim() : this.props.value;

        const options =
            this.props.name === NODE_NAME_ADDITIONAL_GUEST_COUNT ? this.getBringingAGuestOptions() : this.getOptions();

        const shouldRenderUpdateMessage = this.props.name === NODE_NAME_ADDITIONAL_GUEST_COUNT && this.props.isUpdating;

        return (
            <React.Fragment>
                <DropdownComponent
                    name={this.props.nodeId}
                    label={this.renderLabel()}
                    helperText={this.renderHelperText()}
                    onChange={this.handleChange}
                    onBlur={this.props.onBlur}
                    // The unicode placeholder adds a space to the placeholder to prevent the dropdown from picking the first option on mobile by default
                    // The placeholder adds a hidden option to the dropdown that is not selectable in mobile
                    placeholder={this.props.placeholder || '\u00AD'}
                    value={trimmedValue}
                    disabled={this.props.disabled}
                    variation={!this.props.touched && this.props.error ? 'error' : ''}
                    fullWidth
                    aria-required={this.props.required || undefined}
                    data-qa={`${BASE_CLASS_NAME}__${this.props.code}`}
                    data-field-name={this.props.code}
                >
                    {options
                        .map((option) => {
                            const { id, label, value, available, responseLimit } = option;
                            const optionLabel = this.getOptionLabel(label, showRemainingResponses ? available : null);

                            return (
                                !this.isOptionHidden(available) && (
                                    <SuiDropdownOption
                                        key={id}
                                        value={value}
                                        displayValue={optionLabel}
                                        disabled={
                                            responseLimit !== undefined &&
                                            available <= 0 &&
                                            this.props.initialValue !== value
                                        }
                                        data-qa={`${BASE_CLASS_NAME}__option-${value}`}
                                    />
                                )
                            );
                        })
                        .filter(Boolean)}
                </DropdownComponent>
                {shouldRenderUpdateMessage && (
                    <Message data-qa={`${BASE_CLASS_NAME}__${this.props.code}-message`}>
                        {UPDATE_ADDITIONAL_GUEST_COUNT_MESSAGE}
                    </Message>
                )}
            </React.Fragment>
        );
    }

    /**
     * @param {SyntheticEvent} event
     * @param {Object} checkboxes
     */
    handleMultiSelectChange = (event, checkboxes) => {
        const selectedCheckboxes = Object.keys(checkboxes).filter((value) => checkboxes[value]);
        const value = selectedCheckboxes.join(MULTIPLE_SELECTED_VALUE_SEPARATOR);

        this.props.setFieldValue(this.props.nodeId, value);
    };

    /**
     * @param {string} optionValue
     * @return {boolean}
     */
    isMultiSelectValueChecked = (optionValue) => {
        const selectedValues = (this.props.value || '').split(MULTIPLE_SELECTED_VALUE_SEPARATOR);
        return selectedValues.includes(optionValue);
    };

    renderMultiSelect() {
        const CheckBoxGroupComponent = this.props.includeOther ? CheckboxIncludeOther : SuiCheckboxGroup;
        const showRemainingResponses = get(this.props.config, 'showRemainingResponses', true);

        // @TODO We are doing this because we have values that are not trimmed for now.
        // Remove the trimming once the fix has been live for a few months, around March 2019.
        // Jira ticket: FOR-3311.
        const trimmedValue = typeof this.props.value === 'string' ? this.props.value.trim() : this.props.value;

        return (
            <CheckBoxGroupComponent
                name={this.props.nodeId}
                label={this.renderLabel()}
                questionLabel={this.props.includeOther ? this.props.label : undefined}
                helperText={this.renderHelperText()}
                onChange={this.handleMultiSelectChange}
                onBlur={this.props.onBlur}
                value={trimmedValue}
                disabled={this.props.disabled}
                variation={!this.props.touched && this.props.error ? 'error' : ''}
                fullWidth
                aria-required={this.props.required || undefined}
                data-qa={`${BASE_CLASS_NAME}__${this.props.code}`}
                data-field-name={this.props.code}
            >
                {this.getOptions().map((option) => {
                    const { id, label, value, available, responseLimit } = option;
                    const optionLabel = this.getOptionLabel(label, showRemainingResponses ? available : null);

                    return (
                        !this.isOptionHidden(available) && (
                            <SuiCheckbox
                                key={id}
                                name={value}
                                label={optionLabel}
                                disabled={
                                    responseLimit !== undefined &&
                                    available <= 0 &&
                                    !this.props.initialValue.includes(value)
                                }
                                checked={value && this.isMultiSelectValueChecked(value)}
                                baselineAlignment
                                data-qa={`${BASE_CLASS_NAME}__${this.props.code}`}
                                data-field-name={this.props.code}
                            />
                        )
                    );
                })}
            </CheckBoxGroupComponent>
        );
    }

    renderText(htmlType = 'text', additionalProps = {}) {
        const updateAdditionalGuestEmailMessage =
            htmlType === INPUT_TYPE_EMAIL &&
            NODE_NAME_ADDITIONAL_GUEST_EMAIL_REGEXP.test(this.props.name) &&
            this.props.isUpdating;

        return (
            <React.Fragment>
                <SuiTextInput
                    {...additionalProps}
                    name={this.props.nodeId}
                    label={this.renderLabel()}
                    helperText={this.renderHelperText()}
                    onChange={this.handleChange}
                    onBlur={this.handleInputBlur}
                    value={this.props.value}
                    disabled={this.props.disabled}
                    placeholder={this.props.placeholder}
                    variation={!this.props.touched && this.props.error ? 'error' : ''}
                    autoComplete="off"
                    fullWidth
                    aria-required={this.props.required || undefined}
                    type={htmlType}
                    data-qa={`${BASE_CLASS_NAME}__${htmlType}-${this.props.code}`}
                    data-field-name={this.props.code}
                />
                {updateAdditionalGuestEmailMessage && (
                    <Message data-qa={`${BASE_CLASS_NAME}__${this.props.code}-message`}>
                        {UPDATE_ADDITIONAL_GUEST_EMAIL_MESSAGE}
                    </Message>
                )}
            </React.Fragment>
        );
    }

    renderEmail() {
        return this.renderText('email');
    }

    renderPhoneNumber() {
        return this.renderText('tel');
    }

    renderUrl() {
        return this.renderText('url');
    }

    renderNumber() {
        return this.renderText('number', {
            max: get(this.props.config, NUMBER_MAX),
            min: get(this.props.config, NUMBER_MIN),
            step: '0.01',
        });
    }

    renderCaptcha() {
        return (
            <Recaptcha
                onVerify={(token) => {
                    this.props.setFieldValue(this.props.nodeId, token);
                }}
                onExpired={() => {
                    this.props.setFieldValue(this.props.nodeId, null);
                }}
                reset={!this.props.value}
            />
        );
    }

    renderEventOpsResources() {
        return (
            <EventOpsResources
                username={get(this.props.config, 'eventOpsUsername', '')}
                password={get(this.props.config, 'eventOpsPassword', '')}
                clientId={get(this.props.config, 'eventOpsClientId', '')}
                clientSecret={get(this.props.config, 'eventOpsClientSecret', '')}
                startDateNodeCode={get(this.props.config, 'eventOpsStartDateNodeCode')}
                startTimeNodeCode={get(this.props.config, 'eventOpsStartTimeNodeCode')}
                endDateNodeCode={get(this.props.config, 'eventOpsEndDateNodeCode')}
                endTimeNodeCode={get(this.props.config, 'eventOpsEndTimeNodeCode')}
                timeZoneNodeCode={get(this.props.config, 'eventOpsTimeZoneNodeCode')}
                widgetEnv={get(this.props.config, 'eventOpsWidgetEnv')}
                onChange={(lockedResources) => {
                    if (lockedResources == null) {
                        this.props.setFieldValue(this.props.nodeId, null);
                    } else {
                        const sectionIds = lockedResources.map((resource) => resource.section);
                        this.props.setFieldValue(this.props.nodeId, sectionIds.join(','));
                    }
                }}
                /* note that `value` isn't passed because this component has its own state */
            />
        );
    }

    renderEventOps2() {
        return (
            <EventOps2
                authToken={get(this.props.config, 'eventOpsAuthToken', '')}
                clientId={get(this.props.config, 'eventOpsClientId', '')}
                clientSecret={get(this.props.config, 'eventOpsClientSecret', '')}
                companyToken={get(this.props.config, 'eventOpsCompanyToken', '')}
                startDateNodeCode={get(this.props.config, 'eventOpsStartDateNodeCode')}
                startTimeNodeCode={get(this.props.config, 'eventOpsStartTimeNodeCode')}
                endDateNodeCode={get(this.props.config, 'eventOpsEndDateNodeCode')}
                endTimeNodeCode={get(this.props.config, 'eventOpsEndTimeNodeCode')}
                timeZoneNodeCode={get(this.props.config, 'eventOpsTimeZoneNodeCode')}
                locationNodeCode={get(this.props.config, 'eventOpsLocationNodeCode')}
                widgetUrl={get(this.props.config, 'eventOpsWidgetUrl')}
                resourceType={get(this.props.config, 'eventOpsResourceType')}
                onChange={(lockedResources) => {
                    if (lockedResources == null) {
                        this.props.setFieldValue(this.props.nodeId, null);
                    } else {
                        const stringifiedLockedResources =
                            typeof lockedResources !== 'string' ? JSON.stringify(lockedResources) : lockedResources;
                        this.props.setFieldValue(this.props.nodeId, stringifiedLockedResources);
                    }
                }}
                /* note that `value` isn't passed because this component has its own state */
            />
        );
    }

    renderSocialRsvp() {
        const { config = {} } = this.props;
        const selectedSocialNetwork = Object.keys(config).find((socialNetwork) => config[socialNetwork].enabled);

        return (
            <SocialRsvp
                nodeId={this.props.nodeId}
                isRequired={this.props.required}
                selectedSocialNetwork={selectedSocialNetwork}
            />
        );
    }

    /**
     * @param {string} label
     * @param {number} [available]
     * @return {string}
     */
    getOptionLabel(label, available = undefined) {
        if (Number.isFinite(available)) {
            const extraCopy = available <= 0 ? `(${NOT_AVAILABLE})` : `(${SOME_REMAINING(available)})`;
            return `${label} ${extraCopy}`;
        }
        return label;
    }

    renderComponentNotFound() {
        if (__DEVELOPMENT__) {
            return <div>⚠️ Work In Progress: {this.props.inputType}</div>;
        }
        return null;
    }

    ViewWrapperQuestion = ({ visible, children }) => <QuestionView visible={visible}>{children}</QuestionView>;

    ViewWrapperFragment = ({ children }) => <React.Fragment>{children}</React.Fragment>;

    render() {
        if (!this.renderComponentByType[this.props.inputType]) {
            return this.renderComponentNotFound();
        }

        const hasVisibleView = this.props.inputType !== INPUT_TYPE_HIDDEN;
        const ViewWrapper = hasVisibleView ? this.ViewWrapperQuestion : this.ViewWrapperFragment;

        return (
            <ViewWrapper {...this.props}>{this.renderComponentByType[this.props.inputType].apply(this)}</ViewWrapper>
        );
    }
}
